import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useReducedMotion } from 'framer-motion';

/**
 * TENSION Cinematic Background System  v2.1
 * Tuned for premium atmosphere without cyberpunk overload.
 * All opacities calibrated to feel immersive but never distracting.
 */

interface AmbientBackgroundProps {
  variant?: 'hero' | 'section' | 'minimal';
  className?: string;
}

export function AmbientBackground({ variant = 'hero', className = '' }: AmbientBackgroundProps) {
  const prefersReducedMotion = useReducedMotion();

  const { scrollY } = useScroll();

  /* Parallax depths — very subtle, premium not dramatic */
  const rawY0 = useTransform(scrollY, [0, 1200], [0, -90]);
  const rawY1 = useTransform(scrollY, [0, 1200], [0, -60]);
  const rawY2 = useTransform(scrollY, [0, 1200], [0, -36]);

  const y0 = useSpring(rawY0, { stiffness: 45, damping: 24 });
  const y1 = useSpring(rawY1, { stiffness: 48, damping: 24 });
  const y2 = useSpring(rawY2, { stiffness: 52, damping: 26 });

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      style={{ backgroundColor: '#030712' }}
    >
      {/* ── Layer 0: Cinematic scene base — subtle, not blown out ── */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={prefersReducedMotion ? undefined : { y: y0 }}
      >
        <div
          className="absolute inset-[-5%]"
          style={{
            backgroundImage: 'url(/assets/tension/hero/homepage-final-hero-scene.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center 22%',
            /* Reduced opacity for restraint — was 0.62 */
            opacity: 0.46,
            mixBlendMode: 'screen',
          }}
        />
      </motion.div>

      {/* ── Layer 1: City horizon atmosphere ── */}
      {(variant === 'hero' || variant === 'section') && (
        <motion.div
          className="absolute inset-0 will-change-transform"
          style={prefersReducedMotion ? undefined : { y: y1 }}
        >
          <div
            className="absolute bottom-0 inset-x-0 h-[58%]"
            style={{
              backgroundImage: 'url(/assets/tension/hero/hero-city-horizon-glow.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center bottom',
              /* Reduced from 0.44 */
              opacity: 0.32,
              mixBlendMode: 'screen',
            }}
          />
        </motion.div>
      )}

      {/* ── Layer 2: Water reflection — bottom atmosphere ── */}
      {variant === 'hero' && (
        <motion.div
          className="absolute inset-0 will-change-transform"
          style={prefersReducedMotion ? undefined : { y: y2 }}
        >
          <div
            className="absolute bottom-0 inset-x-0 h-[42%]"
            style={{
              backgroundImage: 'url(/assets/tension/hero/hero-water-reflection.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center bottom',
              /* Reduced from 0.30 */
              opacity: 0.20,
              mixBlendMode: 'screen',
            }}
          />
        </motion.div>
      )}

      {/* ── Layer 3: Ambient orb — felt not seen ── */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={prefersReducedMotion ? undefined : { y: y1 }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/assets/tension/backgrounds/ambient-orb-main.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center 35%',
            /* Further reduced — atmospheric not visible */
            opacity: 0.15,
            mixBlendMode: 'screen',
          }}
        />
      </motion.div>

      {/* ── Layer 4+5: Energy waves — very subtle framing elements ── */}
      {variant === 'hero' && (
        <>
          <motion.div
            className="absolute left-0 top-0 h-full w-[52%] will-change-transform"
            style={prefersReducedMotion ? undefined : { y: y2 }}
          >
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: 'url(/assets/tension/hero/hero-energy-waves-left.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'left center',
                /* Reduced from 0.48 — these were too dominant */
                opacity: 0.32,
                mixBlendMode: 'screen',
              }}
            />
          </motion.div>

          <motion.div
            className="absolute right-0 top-0 h-full w-[52%] will-change-transform"
            style={prefersReducedMotion ? undefined : { y: y2 }}
          >
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: 'url(/assets/tension/hero/hero-energy-waves-right.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'right center',
                opacity: 0.32,
                mixBlendMode: 'screen',
              }}
            />
          </motion.div>
        </>
      )}

      {/* ── Glass noise — micro-texture at ~3% for film grain depth ── */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/assets/tension/backgrounds/glass-noise-overlay.png)',
          backgroundSize: '320px 320px',
          backgroundRepeat: 'repeat',
          opacity: 0.032,
          mixBlendMode: 'overlay',
        }}
      />

      {/* ── Cinematic vignette — tight edges, open center ── */}
      <div
        className="absolute inset-0"
        style={{
          background: [
            /* Tight circular center clear zone */
            'radial-gradient(ellipse 72% 55% at 50% 50%, transparent 45%, rgba(3,7,18,.60) 100%)',
            /* Top fade in from brand void */
            'linear-gradient(180deg, rgba(3,7,18,.40) 0%, transparent 18%, transparent 78%, rgba(3,7,18,.90) 100%)',
          ].join(', '),
        }}
      />
    </div>
  );
}

/**
 * Per-section ambient color bloom.
 * Restrained — adds color atmosphere without overpowering.
 */
export function SectionGlow({
  color = 'violet',
  position = 'center',
  intensity = 'normal',
}: {
  color?: 'violet' | 'fuchsia' | 'cyan' | 'blue';
  position?: 'left' | 'center' | 'right';
  intensity?: 'soft' | 'normal';
}) {
  const colorMap: Record<string, string> = {
    violet:  intensity === 'soft' ? 'rgba(155,79,232,.09)'  : 'rgba(155,79,232,.12)',
    fuchsia: intensity === 'soft' ? 'rgba(232,23,155,.08)'  : 'rgba(232,23,155,.11)',
    cyan:    intensity === 'soft' ? 'rgba(0,168,240,.08)'   : 'rgba(0,168,240,.10)',
    blue:    intensity === 'soft' ? 'rgba(26,95,232,.08)'   : 'rgba(26,95,232,.11)',
  };
  const x = position === 'left' ? '18%' : position === 'right' ? '82%' : '50%';

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 bottom-0"
      style={{
        background: `radial-gradient(ellipse 65% 55% at ${x} 50%, ${colorMap[color]}, transparent 72%)`,
        zIndex: 0,
      }}
    />
  );
}
